import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Custom CSS for scrollbars
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const EmailManagement = ({ user }) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [globalSettings, setGlobalSettings] = useState(null);
  const [hoaOverview, setHoaOverview] = useState([]);
  const [emailTypes, setEmailTypes] = useState([]);
  const [selectedHOA, setSelectedHOA] = useState(null);
  const [hoaSettings, setHoaSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailType, setTestEmailType] = useState('payment_reminders');

  useEffect(() => {
    loadEmailTypes();
    loadStats();
    if (activeSubTab === 'global') {
      loadGlobalSettings();
    } else if (activeSubTab === 'hoas') {
      loadHoaOverview();
    }
  }, [activeSubTab]);

  const loadGlobalSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform-admin/emails/global');
      setGlobalSettings(response.data);
    } catch (error) {
      console.error('Error loading global settings:', error);
      setError('Failed to load global email settings');
    } finally {
      setLoading(false);
    }
  };

  const loadHoaOverview = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform-admin/emails/hoas/overview');
      setHoaOverview(response.data.hoas);
    } catch (error) {
      console.error('Error loading HOA overview:', error);
      setError('Failed to load HOA email overview');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailTypes = async () => {
    try {
      const response = await axios.get('/api/platform-admin/emails/types');
      setEmailTypes(response.data.email_types);
    } catch (error) {
      console.error('Error loading email types:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('/api/platform-admin/emails/stats');
      setStats(response.data.statistics);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadHoaSettings = async (hoaId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/platform-admin/emails/hoa/${hoaId}`);
      setHoaSettings(response.data);
    } catch (error) {
      console.error('Error loading HOA settings:', error);
      setError('Failed to load HOA email settings');
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalSettings = async (settings) => {
    try {
      setLoading(true);
      await axios.put('/api/platform-admin/emails/global', settings);
      alert('Global email settings updated successfully!');
      loadGlobalSettings();
    } catch (error) {
      console.error('Error updating global settings:', error);
      alert('Failed to update global email settings');
    } finally {
      setLoading(false);
    }
  };

  const updateHoaSettings = async (hoaId, settings) => {
    try {
      setLoading(true);
      await axios.put(`/api/platform-admin/emails/hoa/${hoaId}`, settings);
      alert('HOA email settings updated successfully!');
      loadHoaSettings(hoaId);
      loadHoaOverview();
    } catch (error) {
      console.error('Error updating HOA settings:', error);
      alert('Failed to update HOA email settings');
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/platform-admin/emails/test', {
        test_email: testEmail,
        email_type: testEmailType,
        hoa_id: selectedHOA
      });
      alert(`Test ${testEmailType} email sent to ${testEmail}!`);
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email');
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateHOAs = async (emailType, settings) => {
    try {
      setLoading(true);
      const targetHOAs = hoaOverview
        .filter(hoa => hoa.selected)
        .map(hoa => hoa.hoa_id);
      
      await axios.post('/api/platform-admin/emails/bulk-update', {
        target_hoas: targetHOAs.length > 0 ? targetHOAs : [],
        email_type: emailType,
        settings: settings
      });
      
      alert(`Bulk update completed for ${targetHOAs.length || 'all'} HOAs!`);
      loadHoaOverview();
    } catch (error) {
      console.error('Error in bulk update:', error);
      alert('Failed to perform bulk update');
    } finally {
      setLoading(false);
    }
  };

  const subTabs = [
    { id: 'overview', name: 'Overview', icon: '📊' },
    { id: 'global', name: 'Global Settings', icon: '🌐' },
    { id: 'hoas', name: 'HOA Settings', icon: '🏘️' },
    { id: 'templates', name: 'Email Templates', icon: '📧' },
    { id: 'test', name: 'Test Emails', icon: '🧪' }
  ];

  return (
    <div className="p-6">
      <style>{scrollbarStyles}</style>
      
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Management</h2>
        <p className="text-gray-600">
          Manage email services and notifications across all HOAs
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Sub-tab Navigation - Mobile Friendly */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <nav className="flex space-x-4 md:space-x-8 min-w-max px-1">
          {subTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`py-2 px-3 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeSubTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon} {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content - Mobile Friendly Container */}
      <div className="overflow-hidden"> {/* Prevent horizontal overflow */}
      {activeSubTab === 'overview' && (
        <OverviewTab stats={stats} hoaOverview={hoaOverview} loading={loading} />
      )}

      {activeSubTab === 'global' && (
        <GlobalSettingsTab 
          settings={globalSettings}
          emailTypes={emailTypes}
          onUpdate={updateGlobalSettings}
          loading={loading}
        />
      )}

      {activeSubTab === 'hoas' && (
        <HOASettingsTab
          hoaOverview={hoaOverview}
          selectedHOA={selectedHOA}
          hoaSettings={hoaSettings}
          emailTypes={emailTypes}
          onSelectHOA={(hoaId) => {
            setSelectedHOA(hoaId);
            loadHoaSettings(hoaId);
          }}
          onUpdate={updateHoaSettings}
          onBulkUpdate={bulkUpdateHOAs}
          loading={loading}
        />
      )}

      {activeSubTab === 'templates' && (
        <EmailTemplatesTab
          emailTypes={emailTypes}
          globalSettings={globalSettings}
          onUpdate={updateGlobalSettings}
          loading={loading}
        />
      )}

      {activeSubTab === 'test' && (
        <TestEmailTab
          testEmail={testEmail}
          setTestEmail={setTestEmail}
          testEmailType={testEmailType}
          setTestEmailType={setTestEmailType}
          emailTypes={emailTypes}
          onSendTest={sendTestEmail}
          loading={loading}
        />
      )}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats, hoaOverview, loading }) => {
  if (loading) return <div className="text-center py-8">Loading overview...</div>;

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-blue-100 text-blue-600">
            🏘️
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Total HOAs</p>
            <p className="text-2xl font-semibold text-gray-900">{stats?.total_hoas || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-green-100 text-green-600">
            ⚙️
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Custom Settings</p>
            <p className="text-2xl font-semibold text-gray-900">{stats?.hoas_with_custom_settings || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-purple-100 text-purple-600">
            🌐
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Using Global</p>
            <p className="text-2xl font-semibold text-gray-900">{stats?.hoas_using_global_settings || 0}</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center">
          <div className={`p-3 rounded-full ${stats?.global_email_enabled ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
            📧
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Global Status</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats?.global_email_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Global Settings Tab Component
const GlobalSettingsTab = ({ settings, emailTypes, onUpdate, loading }) => {
  const [formData, setFormData] = useState(settings);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  if (loading || !formData) return <div className="text-center py-8">Loading global settings...</div>;

  const handleServiceConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      service_config: {
        ...prev.service_config,
        [field]: value
      }
    }));
  };

  const handleEmailTypeToggle = (emailType, enabled) => {
    setFormData(prev => {
      const emailTypes = prev.email_types || [];
      const existingIndex = emailTypes.findIndex(et => et.email_type === emailType);
      
      let updatedEmailTypes;
      if (existingIndex >= 0) {
        // Update existing
        updatedEmailTypes = emailTypes.map((et, index) => 
          index === existingIndex ? { ...et, enabled } : et
        );
      } else {
        // Add new entry
        updatedEmailTypes = [...emailTypes, {
          email_type: emailType,
          enabled,
          template: settings.email_types?.find(et => et.email_type === emailType)?.template || {},
          send_immediately: true,
          retry_attempts: 3,
          hoa_can_override: true,
          description: `Settings for ${emailType.replace('_', ' ')}`
        }];
      }
      
      return {
        ...prev,
        email_types: updatedEmailTypes
      };
    });
  };

  const handleSave = () => {
    onUpdate({
      service_config: formData.service_config,
      email_types: formData.email_types,
      enabled: formData.global_enabled
    });
  };

  return (
    <div className="space-y-6">
      {/* Service Configuration */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Email Service Configuration</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Provider
            </label>
            <select
              value={formData.service_config?.provider || 'sendgrid'}
              onChange={(e) => handleServiceConfigChange('provider', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="sendgrid">SendGrid</option>
              <option value="ses">Amazon SES</option>
              <option value="smtp">SMTP</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key / Password
            </label>
            <input
              type="password"
              value={formData.service_config?.api_key || ''}
              onChange={(e) => handleServiceConfigChange('api_key', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter API key or password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Email
            </label>
            <input
              type="email"
              value={formData.service_config?.from_email || ''}
              onChange={(e) => handleServiceConfigChange('from_email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="noreply@hoaplatform.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Name
            </label>
            <input
              type="text"
              value={formData.service_config?.from_name || ''}
              onChange={(e) => handleServiceConfigChange('from_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="HOA Platform"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center">
          <input
            type="checkbox"
            id="global-enabled"
            checked={formData.global_enabled || false}
            onChange={(e) => setFormData(prev => ({ ...prev, global_enabled: e.target.checked }))}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="global-enabled" className="ml-2 block text-sm text-gray-900">
            Enable global email system
          </label>
        </div>
      </div>

      {/* Email Types Management */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Email Types Configuration</h3>
        
        <div className="space-y-4">
          {emailTypes.map(emailType => {
            const typeSettings = formData.email_types?.find(et => et.email_type === emailType.type);
            const isEnabled = typeSettings?.enabled !== false;
            
            return (
              <div key={emailType.type} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{emailType.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">{emailType.description}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => handleEmailTypeToggle(emailType.type, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${
                      isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                    }`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        isEnabled ? 'translate-x-5' : 'translate-x-0.5'
                      } mt-0.5`}></div>
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Disabling an email type will prevent it from being sent globally. 
            Individual HOAs can still override these settings if allowed.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Global Settings'}
        </button>
      </div>
    </div>
  );
};

// HOA Settings Tab Component
const HOASettingsTab = ({ hoaOverview, selectedHOA, hoaSettings, emailTypes, onSelectHOA, onUpdate, onBulkUpdate, loading }) => {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* HOA List - Mobile Optimized */}
      <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Select HOA</h3>
        
        <div className="space-y-2 max-h-80 md:max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {hoaOverview.map(hoa => (
            <div
              key={hoa.hoa_id}
              onClick={() => onSelectHOA(hoa.hoa_id)}
              className={`p-3 border rounded cursor-pointer transition-colors ${
                selectedHOA === hoa.hoa_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{hoa.hoa_name}</div>
                  <div className="text-sm text-gray-500 truncate">ID: {hoa.hoa_id}</div>
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  {hoa.has_custom_settings && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      Custom
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    hoa.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {hoa.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* HOA Settings Panel - Mobile Optimized */}
      <div className="bg-white p-4 md:p-6 rounded-lg shadow border">
        {selectedHOA && hoaSettings ? (
          <HOASettingsPanel
            hoaId={selectedHOA}
            settings={hoaSettings}
            emailTypes={emailTypes}
            onUpdate={onUpdate}
            loading={loading}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-4">📧</div>
            <p>Select an HOA to view and edit email settings</p>
          </div>
        )}
      </div>
    </div>
  );
};

// HOA Settings Panel Component
const HOASettingsPanel = ({ hoaId, settings, emailTypes, onUpdate, loading }) => {
  const [formData, setFormData] = useState(settings);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleSave = () => {
    onUpdate(hoaId, {
      enabled: formData.enabled,
      service_config: formData.service_config
    });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">HOA Email Settings</h3>
      
      <div className="flex items-center">
        <input
          type="checkbox"
          id="hoa-enabled"
          checked={formData.enabled}
          onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="hoa-enabled" className="ml-2 block text-sm text-gray-900">
          Enable email system for this HOA
        </label>
      </div>

      <div className="text-sm text-gray-600">
        {formData.service_config ? 
          'This HOA has custom email service configuration.' : 
          'This HOA uses global email service configuration.'
        }
      </div>

      <button
        onClick={handleSave}
        disabled={loading}
        className="px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {loading ? 'Saving...' : 'Save Settings'}
      </button>
    </div>
  );
};

// Email Templates Tab Component
const EmailTemplatesTab = ({ emailTypes, globalSettings, onUpdate, loading }) => {
  const [selectedEmailType, setSelectedEmailType] = useState(emailTypes[0]?.type);
  
  const selectedTemplate = globalSettings?.email_types?.find(et => et.email_type === selectedEmailType);

  return (
    <div className="grid lg:grid-cols-1 gap-6">
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Email Templates</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Email Type
          </label>
          <select
            value={selectedEmailType}
            onChange={(e) => setSelectedEmailType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {emailTypes.map(type => (
              <option key={type.type} value={type.type}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {selectedTemplate && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject Line
              </label>
              <input
                type="text"
                value={selectedTemplate.template.subject}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                HTML Body (Read Only)
              </label>
              <textarea
                value={selectedTemplate.template.html_body}
                readOnly
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Variables
              </label>
              <div className="flex flex-wrap gap-2">
                {selectedTemplate.template.variables.map(variable => (
                  <span key={variable} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                    {`{${variable}}`}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Email templates are currently read-only. Template customization will be available in a future update.
          </p>
        </div>
      </div>
    </div>
  );
};

// Test Email Tab Component
const TestEmailTab = ({ testEmail, setTestEmail, testEmailType, setTestEmailType, emailTypes, onSendTest, loading }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow border max-w-2xl">
      <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Test Email Address
          </label>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="test@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Type
          </label>
          <select
            value={testEmailType}
            onChange={(e) => setTestEmailType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {emailTypes.map(type => (
              <option key={type.type} value={type.type}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={onSendTest}
          disabled={loading || !testEmail}
          className="px-6 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Sending...' : 'Send Test Email'}
        </button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Test Email Features:</strong>
        </p>
        <ul className="mt-2 text-sm text-blue-700 list-disc list-inside">
          <li>Uses current global email service configuration</li>
          <li>Sends with sample data for template variables</li>
          <li>Helps verify email delivery and formatting</li>
          <li>Check your spam folder if email doesn't arrive</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailManagement;